import { AppConstants } from 'app/app.constants';

/**
 * Returns the canonical device model name used by `DEVICE_MODEL` comparisons,
 * `tasks.equipment.*` translations and equipment images.
 *
 * Only known aliases are rewritten. Other values are returned untouched because casing
 * is meaningful for some models (e.g. 'Agronave' identifies IsoView-format tasks).
 */
export function normalizeDeviceModel(model: string): string {
  if (
    Object.prototype.hasOwnProperty.call(
      AppConstants.DEVICE_MODEL_ALIASES,
      model,
    )
  ) {
    return AppConstants.DEVICE_MODEL_ALIASES[model];
  }
  return model;
}

const { AGRONAVE, AGRONAVE7, AGRONAVE12, AGRONAVE12W } =
  AppConstants.DEVICE_MODEL;

/** Canonical agroNave model names, to compare with `normalizeDeviceModel` output. */
export const AGRONAVE_MODEL_NAMES: readonly string[] = [
  AGRONAVE,
  AGRONAVE7,
  AGRONAVE12,
  AGRONAVE12W,
].map(({ name }) => name);

/** Legacy task model that the API's `SPECIFIC_MODEL_TO_GENERIC` also maps to agroNave. */
const LOCAL_AGRONAVE_MODEL = 'LOCAL';

/**
 * Tells whether a persisted task model belongs to an agroNave device.
 *
 * Accepts the persisted spellings (e.g. 'agronave12W') without normalizing first.
 * @remarks Mirrors the agroNave entries of the API's `SPECIFIC_MODEL_TO_GENERIC`, so a new
 * agroNave model only needs a `DEVICE_MODEL` entry here. 'Agronave' (capital A) identifies
 * IsoView-format tasks and is not agroNave.
 */
export function isAgronaveModel(model: string): boolean {
  return (
    model === LOCAL_AGRONAVE_MODEL ||
    AGRONAVE_MODEL_NAMES.includes(normalizeDeviceModel(model))
  );
}
